import { Form, Modal, Typography } from "antd";
import moment from "moment";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Socket } from "socket.io-client";

import {
  getConfigurationFee,
  getExchangeRate,
  useUpdateSendMoney,
  useUpdateStatusSendMoney,
} from "../../../../api/send-money";
import { useGetAvlBalanceInternal } from "../../../../api/withdrawal";
import { CustomButton } from "../../../../components/button/buttonCustom";
import { ButtonTransparent } from "../../../../components/button/buttonTransparent";
import { purposeOfRemittanceItemNames } from "../../../../components/inputs/select/purposeOfRemittance.constants";
import ConfirmModal from "../../../../components/modal/confirmModal";
import { useNotification } from "../../../../components/providers/notification/useNotification";
import {
  Crypto,
  Fiat,
} from "../../../../pages/send-money/component/form-transfer-props";
import PaymentStatus from "../../../../types/paymentStatus.type";
import { useAllMetadataCurrency } from "../../../../utils/hooks/useAllMetadataCurrency";
import useResponsive from "../../../../utils/hooks/useResponsive";
import { shortenAddress } from "../../../../utils/list.utils";
import StatusForm from "./status-form";
import TextContent from "./text-content";
import FormFutureDate from "../../../../pages/send-money/component/form-future-date";
import { ButtonOutlinedCustom } from "../../../../components/button/buttonOulined";
import { Direction } from "../../../../pages/send-money/component/form-transfer-v2";

const FormSendMoney = ({
  orderId,
  refetch,
  socket,
  data,
  refetchDetail,
}: {
  orderId: string;
  refetch: any;
  socket: Socket<any, any>;
  data: any;
  refetchDetail: any;
}) => {
  const { isTablet } = useResponsive();
  const { open: openNotification } = useNotification();
  const navigate = useNavigate();

  const [idCancel, setIdCancel] = useState(null);
  const [actionFutureDate, setActionFutureDate] = useState(false);
  const [disabledPortfolio, setDisabledPortfolio] = useState(false)
  const [showReviewFee, setShowReviewFee] = useState(false)
  const [paymentCurrency, setPaymentCurrency] = useState<number | undefined>();
  const [receiveCurrency, setReceiveCurrency] = useState<number | undefined>();

  const [formFutureDate] = Form.useForm();

  const { dataAllCurrency } = useAllMetadataCurrency()
  const { data: avlBalance, refetch: refetchBalance } =
    useGetAvlBalanceInternal(paymentCurrency)

  const { mutate: updateSendMoney, isLoading: isLoadingUpdate } =
    useUpdateStatusSendMoney({
      onSuccess: () => {
        refetch();
        refetchDetail();
      },
      onError: (error: { message: string; reason_code: string }) => {
        if (error?.reason_code === 'ERR00104') {
          setShowReviewFee(true)
          return;
        } else if (openNotification) {
          openNotification({
            key: "add_bank_account_failure",
            message: error?.message || "Something went wrong!",
            type: "error",
          });
        }
      },
    });

  const { mutate: updateOrderWithLatestFee, isLoading: isLoadingUpdateOrder } =
    useUpdateSendMoney({
      onSuccess: () => {
        // After updating with latest fee configuration, refresh data
        refetch();
        refetchDetail();
        setShowReviewFee(false);
        if (openNotification) {
          openNotification({
            key: "update_fee_success",
            message:
              "Order has been updated with the latest fees. Please review and confirm again.",
            type: "success",
          });
        }
      },
      onError: (error: { message: string }) => {
        setShowReviewFee(false)
        if (openNotification) {
          openNotification({
            key: "update_fee_failed",
            message: error?.message || "Something went wrong!",
            type: "error",
          });
        }
      },
    });

  const { mutate: cancelSendMoney, isLoading: isLoadingCancel } =
    useUpdateStatusSendMoney({
      onSuccess: (response: { data: { message: string } }) => {
        if (openNotification) {
          openNotification({
            key: "update_success",
            message: "Send Money order canceled successfully!",
            type: "success",
          });
        }
        refetch();
        refetchDetail();
        setIdCancel(null);
      },
      onError: (error: { message: string }) => {
        if (openNotification) {
          openNotification({
            key: "add_bank_account_failure",
            message: error?.message || "Something went wrong!",
            type: "error",
          });
        }
      },
    });

  const handlePaid = () => {
    updateSendMoney({
      id: Number(orderId),
      isConfirmed: true,
    });
  };

  const handleEdit = () => {
    navigate(`/send-money?orderId=${data?.data?.id}`);
  };

  const handlePayLater = () => {
    setActionFutureDate(true);
  };

  const handleSubmitCancel = () => {
    if (isLoadingCancel) return;
    cancelSendMoney({
      id: Number(orderId),
      status: "CANCELLED",
    });
  };

  const handleCancel = () => {
    if (isLoadingCancel) return;
    setIdCancel(null);
  };

  const reasonStatus = () => {
    switch (data?.data?.status?.current) {
      case PaymentStatus.PENDING:
        return "To finalize your send money, please wait while your request is being reviewed.";
      case PaymentStatus.REJECTED:
        return data?.data?.reason || "N/A";
      case PaymentStatus.POSTPONED:
        return data?.data?.reason || "N/A";
      case PaymentStatus.FAILED:
        return data?.data?.reason || "Something went wrong.";
      case PaymentStatus.APPROVED:
        return "This request was approved by the admin.";
      case PaymentStatus.CANCELLED:
        return "Send money order canceled successfully.";
      case PaymentStatus.COMPLETED:
        return "Send money has been completed. Check your portfolio for details.";
      default:
        return "";
    }
  };

  useEffect(() => {
    if (!dataAllCurrency?.length) return
    if (!orderId || !data?.data) return

    if (data?.data?.paymentCurrency) {
      const paymentCurrencyDefault = dataAllCurrency.find(
        (item: any) => item.symbol === data?.data?.paymentCurrency
      ) as any
      const paymentCurrencyId = paymentCurrencyDefault?.id
      setPaymentCurrency(paymentCurrencyId)
    }

    if (data?.data?.receiveCurrency) {
      const receiveCurrencyDefault = dataAllCurrency.find(
        (item: any) => item.symbol === data?.data?.receiveCurrency
      ) as any
      const receiveCurrencyId = receiveCurrencyDefault?.id
      setReceiveCurrency(receiveCurrencyId)
    }
  }, [dataAllCurrency, orderId, data?.data])

  // Auto refresh detail similar to refetchBalance interval in DirectorTransferDetailV2
  useEffect(() => {
    if (!paymentCurrency) return;
    const intervalId = setInterval(() => {
      refetchBalance();
    }, 60000);
    return () => clearInterval(intervalId);
  }, [data?.data?.paymentCurrency, paymentCurrency, refetchBalance]);

  useEffect(() => {
    if (!avlBalance) return;
    const totalBalanceString = avlBalance?.data?.data?.totalBalance ? avlBalance?.data?.data?.totalBalance : 0
    const totalSendAmountString = data?.data?.amounts?.totalSendAmount ? data?.data?.amounts?.totalSendAmount.replace(/,/g, "") : 0
    const senderAmountString = data?.data?.amounts?.senderAmount ? data?.data?.amounts?.senderAmount.replace(/,/g, "") : 0
    const totalBalance = Number(totalBalanceString);
    const totalSendAmount = Number(totalSendAmountString);
    const senderAmount = Number(senderAmountString);
    const requiredAmount = totalSendAmount > 0 ? totalSendAmount : (senderAmount > 0 ? senderAmount : Number(data?.data?.amount ?? 0) || 0);

    setDisabledPortfolio(totalBalance === 0 || totalBalance < requiredAmount);
  }, [
    avlBalance,
    data?.data?.amounts?.senderAmount,
    data?.data?.amounts?.totalSendAmount,
    data?.data?.amount,
  ]);

  const renderButton = () => {
    const status = data?.data?.status?.current;
    const isPaid = data?.data?.isConfirmed;
    const remiterType = data?.data?.remiter?.type;
    const paymentId = data?.data?.id;

    const shouldRender = !(
      (status !== PaymentStatus.PENDING &&
        status !== PaymentStatus.FAILED &&
        (isPaid || remiterType === "INTERNAL")) ||
      status === PaymentStatus.CANCELLED ||
      status === PaymentStatus.COMPLETED
    ) || status === PaymentStatus.CREATED;
    return shouldRender ? (
      <div className="form-history-button">
        {status === PaymentStatus.CREATED ? (
          <>
            {disabledPortfolio && (
              <Typography
                style={{
                  fontSize: isTablet ? 10 : 12,
                  fontWeight: 400,
                  color: '#EC3333',
                }}>
                Your payment currency balance for this request is insufficient. Top up the balance.
              </Typography>
            )}
            <div style={{ display: "flex", gap: 24 }}>
              <ButtonOutlinedCustom
                size={isTablet ? "medium" : "large"}
                text="Edit"
                width="100%"
                onClick={handleEdit}
                isLoading={isLoadingUpdate || isLoadingCancel}
              />
              <CustomButton
                size={isTablet ? "medium" : "large"}
                text="Future date"
                width="100%"
                onClick={() => {
                  if (!isLoadingUpdate && !isLoadingCancel) handlePayLater();
                }}
                isLoading={isLoadingUpdate || isLoadingCancel}
              />
              <CustomButton
                size={isTablet ? "medium" : "large"}
                text="Confirm"
                width="100%"
                onClick={handlePaid}
                isLoading={isLoadingUpdate || isLoadingCancel}
                disabled={disabledPortfolio}
              />
            </div>
          </>
        ) : null}

        {status === PaymentStatus.PENDING || status === PaymentStatus.FAILED || status === PaymentStatus.CREATED ?
          (status === PaymentStatus.CREATED) ? (
            <ButtonTransparent
              size={isTablet ? "medium" : "large"}
              text="Cancel Send Money"
              width="100%"
              textClassName="text-button-dangerous"
              isLoading={isLoadingUpdate || isLoadingCancel}
              onClick={() => setIdCancel(paymentId)}
            />
          ) : (
            <ButtonOutlinedCustom
              size={isTablet ? "medium" : "large"}
              text="Cancel Send Money"
              width="100%"
              className="outline-dangerous"
              isLoading={isLoadingUpdate || isLoadingCancel}
              onClick={() => setIdCancel(paymentId)}
            />
          ) : null}
      </div>
    ) : null;
  };

  useEffect(() => {
    if (!orderId) return;
    const handleDetailUpdate = () => {
      refetchDetail();
    };
    socket.on(`user.paymentRequest.${orderId}.updated`, handleDetailUpdate);

    return () => {
      socket.off(`user.paymentRequest.${orderId}.updated`, handleDetailUpdate);
    };
  }, [socket, orderId, refetchDetail]);

  const handleContinueReview = async () => {
    if (!orderId) return;

    try {
      const paymentCurrencyId = paymentCurrency;
      const receiveCurrencyId = receiveCurrency;
      const detail: any = data?.data;

      // Determine payment decimals same way as in form-transfer-v2
      const paymentCurrencyMeta = dataAllCurrency.find(
        (item: any) => item.id === paymentCurrencyId
      ) as any;
      const paymentDecimal = paymentCurrencyMeta?.decimals ?? 2;

      let latestRate = detail?.amounts?.exchangeRate || 1;
      try {
        const rateRes = await getExchangeRate({
          from: paymentCurrencyId,
          to: receiveCurrencyId,
        });
        latestRate = rateRes?.data?.data ?? latestRate;
      } catch {
        // keep previous rate if latest cannot be fetched
      }

      const feeRes = await getConfigurationFee(receiveCurrencyId);
      const feeData = feeRes?.data?.data;

      const rawReceiverAmount = Number(
        detail?.amounts?.receiverAmount ?? 0
      );

      const rawSenderAmount =
        latestRate && latestRate !== 0 ? rawReceiverAmount / latestRate : rawReceiverAmount;

      const recalculatedSenderAmount = Number(rawSenderAmount.toFixed(paymentDecimal));

      let feeConfigId = feeData?.id;
      const isCryptoReceive = dataAllCurrency.some(
        (item: any) => item.id === receiveCurrencyId && item.type === 'CRYPTO'
      );

      const transferFeeType = isCryptoReceive
        ? feeData?.cryptoTransferFeeType
        : feeData?.fiatTransferFeeType;

      if (transferFeeType === 'DYNAMIC') {
        const networkIdForFee =
          detail?.beneficiary?.networkId ??
          detail?.beneficiary?.network?.id ??
          detail?.provider?.networkId;
        if (networkIdForFee && Array.isArray(feeData?.transferFees)) {
          const matchedTransferFee = feeData.transferFees.find(
            (item: any) => item.networkId === networkIdForFee
          );
          if (matchedTransferFee?.id) {
            feeConfigId = matchedTransferFee.id;
          }
        }
      }

      // Build full body similar to onUpdateOrder in form-transfer-v2
      const body: any = {
        id: Number(orderId),
        senderAmount: recalculatedSenderAmount,
        receiverAmount: rawReceiverAmount,
        direction: detail?.direction ?? Direction.BEN_TO_PAY,
        // core currencies
        receiveCurrencyId: receiveCurrencyId ? Number(receiveCurrencyId) : undefined,
        paymentCurrencyId: paymentCurrencyId ? Number(paymentCurrencyId) : undefined,
        // fee config & rate
        feeConfigVersion: feeData?.version,
        exchangeRate: latestRate,
        usdToFeeCurrencyRate: feeData?.usdToFeeCurrencyRate,
        feeConfigId,
        // timestamps & purpose
        dateOfTransaction: detail?.timestamps?.dateOfTransaction,
        purposeOfRemittance: detail?.purposeOfRemittance,
      };

      // Map additional fields equivalent to create/update in form-transfer-v2
      // Always send keys, use undefined when data not available

      // company / beneficiary
      body.companyId =
        detail?.companyId ?? detail?.beneficiary?.id ?? undefined;
      body.bankAccountId =
        detail?.bankAccountId ?? detail?.beneficiary?.bankAccountId ?? undefined;

      // provider & beneficiary wallets / bank accounts
      body.providerWalletId =
        detail?.providerWalletId ?? detail?.provider?.walletId ?? undefined;
      body.providerBankAccountId =
        detail?.providerBankAccountId ?? detail?.provider?.bankAccountId ?? undefined;
      body.beneficiaryWalletId =
        detail?.beneficiaryWalletId ?? detail?.beneficiary?.walletId ?? undefined;

      // transfer methods & who pays fee
      body.feesPayer = detail?.feePayer ?? 'SENDER';
      body.senderMethod = detail?.senderMethod ?? 'INTERNAL';
      body.receiverMethod =
        detail?.receiverMethod ?? (detail?.receiveUser ? 'INTERNAL' : 'EXTERNAL');

      // service provider & receiver user
      body.serviceProviderId =
        detail?.serviceProviderId ?? detail?.provider?.id ?? undefined;
      body.receiverUserId =
        detail?.receiverUserId ?? detail?.receiveUser?.id ?? undefined;

      // special instructions & documents
      body.specialInstructions = detail?.specialInstructions ?? undefined;
      body.specialInstructionsDetail = detail?.specialInstructionsDetail ?? '';
      body.documents = Array.isArray(detail?.documents) ? detail.documents : [];

      updateOrderWithLatestFee(body);
    } catch (error: any) {
      if (openNotification) {
        openNotification({
          key: "update_fee_failed",
          message: error?.message || "Something went wrong!",
          type: "error",
        });
      }
    }
  }

  return (
    <div>
      <div className="form-history">
        <StatusForm
          status={data?.data?.status?.current}
          content={reasonStatus()}
        />
        <div className="modal-balance">
          <div className="value-balance">
            <Typography style={{ fontSize: 16, color: "white" }}>
              {data?.data?.remiter?.type === "INTERNAL" ||
                data?.data?.isConfirmed ||
                data?.data?.status?.current === PaymentStatus.PENDING ||
                data?.data?.status?.current === PaymentStatus.COMPLETED
                ? "Paid"
                : "Total to pay"}
            </Typography>
            <Typography className="text-gradient">
              {data?.data?.amounts?.totalSendAmount}{" "}
              {data?.data?.paymentCurrency}
            </Typography>
          </div>
        </div>
        <div className="form-history-content">
          <TextContent
            label="Beneficiary Will Receive"
            value={`${data?.data?.amounts?.totalReceiveAmount} ${data?.data?.receiveCurrency}`}
            color="rgba(242, 138, 40, 1)"
          />
        </div>
        <div className="form-history-content border-top">
          <TextContent label="Payment Method" value="Portfolio" />
          <TextContent label="Currency" value={data?.data?.paymentCurrency} />
          <TextContent
            label="Reference Number"
            value={data?.data?.referenceNumber}
            canCopy
            color="rgba(242, 138, 40, 1)"
          />
        </div>

        <div className="form-history-content border-top">
          {data?.data?.receiveUser ? (
            <>
              <TextContent
                label="Transfer method"
                value="SMART UID"
                color="rgba(242, 138, 40, 1)"
              />
              <TextContent
                label="Beneficiary"
                value={data?.data?.receiveUser?.smartUID}
                color="rgba(242, 138, 40, 1)"
              />
            </>
          ) : (
            <>
              <TextContent
                label="Beneficiary"
                value={data?.data?.beneficiary?.companyName}
                color="rgba(242, 138, 40, 1)"
              />
              {Fiat.includes(data?.data?.receiveCurrency) ? (
                <>
                  <TextContent
                    label="Bank Name"
                    value={data?.data?.beneficiary?.bankName}
                    canCopy
                  />
                  <TextContent
                    label="Bank SWIFT"
                    value={data?.data?.beneficiary?.bankSwift}
                    canCopy
                  />
                  <TextContent
                    label="Bank Address"
                    value={data?.data?.beneficiary?.bankAddress}
                    canCopy
                  />
                  <TextContent
                    label="Bank Account"
                    value={data?.data?.beneficiary?.bankAccountNumber}
                    canCopy
                  />
                </>
              ) : (
                <>
                  <TextContent
                    label="Network"
                    value={data?.data?.beneficiary?.network ?? data?.data?.beneficiary?.networkName}
                  />
                  <TextContent
                    label="Beneficiary Wallet Address"
                    value={shortenAddress(
                      data?.data?.beneficiary?.walletAddress
                    )}
                    realValue={data?.data?.beneficiary?.walletAddress}
                    canCopy
                  />
                </>
              )}
            </>
          )}
          <TextContent
            label="Send Date"
            value={moment(data?.data?.timestamps?.dateOfTransaction).format(
              "DD/MM/YYYY"
            )}
          />
          <TextContent
            label="Purpose of Remittance"
            value={
              data?.data?.purposeOfRemittance &&
              purposeOfRemittanceItemNames[
              data?.data
                ?.purposeOfRemittance as keyof typeof purposeOfRemittanceItemNames
              ]
            }
          />
          {data?.data?.futurePaymentDate ? (
            <TextContent
              label="Future date"
              value={data?.data?.futurePaymentDate
                ? moment(data?.data?.futurePaymentDate).format("DD/MM/YYYY")
                : ""
              }
            />
          ) : (
            <></>
          )}
        </div>
        <div className="form-history-content border-top">
          <TextContent
            label="Provider"
            value={data?.data?.provider?.provider}
            color="rgba(242, 138, 40, 1)"
          />
          {Crypto.includes(data?.data?.paymentCurrency) ? (
            <>
              <TextContent
                label="Provider Wallet Network"
                value={data?.data?.provider?.networkName}
                canCopy
              />
              <TextContent
                label="Provider Wallet Address"
                value={shortenAddress(data?.data?.provider?.walletAddress)}
                realValue={data?.data?.provider?.walletAddress}
                canCopy
              />
            </>
          ) : (
            <>
              <TextContent
                label="Provider Bank Name"
                value={data?.data?.provider?.bankName}
                canCopy
              />
              <TextContent
                label="Provider Bank SWIFT"
                value={data?.data?.provider?.bankSwift}
                canCopy
              />
              <TextContent
                label="Provider Bank Address"
                value={data?.data?.provider?.bankAddress}
                canCopy
              />
              <TextContent
                label="Provider Bank Account"
                value={data?.data?.provider?.bankAccountNumber}
                canCopy
              />
            </>
          )}
        </div>
        <div className="form-history-content border-top">
          <TextContent
            label="Order Time"
            value={moment(data?.data?.timestamps?.orderTime).format(
              "HH:mm:ss DD/MM/YYYY"
            )}
          />
        </div>
      </div>
      {renderButton()}
      <ConfirmModal
        type='warning'
        title='Please Confirm Changes'
        description='Fee or beneficiary receive has been updated. Take a moment to review before continuing.'
        open={showReviewFee}
        onCancel={() => null}
        onSubmit={handleContinueReview}
        isLoading={isLoadingUpdateOrder}
        textSubmit='OK'
        showButtonCancel={false}
      />
      <Modal
        open={actionFutureDate}
        onCancel={() => setActionFutureDate(false)}
        footer={null}
        destroyOnClose
        maskClosable={false}
        title="Future date"
        className="edit-wallet-modal"
      >
        <FormFutureDate
          detailSendMoney={data?.data}
          form={formFutureDate}
          handleCancel={() => setActionFutureDate(false)}
          id={String(data?.data?.id || orderId)}
          handleRefetch={refetchDetail}
        />
      </Modal>
      <ConfirmModal
        type="warning"
        title="Cancel send money?"
        description="Are you sure you want to cancel this send money order? This action cannot be undone."
        open={!!idCancel}
        onCancel={handleCancel}
        onSubmit={handleSubmitCancel}
        isLoading={isLoadingCancel}
      />
    </div>
  );
};

export default FormSendMoney;
